'use client';

import Image from 'next/image';
import { type JSX, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles, Skeleton } from '@videoblocks/design-system';

import { HoverOverlay } from './HoverOverlay';
import { SelectedOverlay } from './SelectedOverlay';

export interface ImageResultsCardProps extends React.HTMLAttributes<HTMLDivElement> {
    imageUrl: string;
    imageAlt: string;
    selected?: boolean;
    disabled?: boolean;
    onSelect?: () => void;
    className?: string;
}

export function ImageResultsCard({
    imageUrl,
    imageAlt,
    selected = false,
    disabled = false,
    onSelect,
    className,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onKeyDown,
    ...rest
}: ImageResultsCardProps): JSX.Element {
    const [isHovering, setIsHovering] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div
            className={twMerge(
                `relative size-full overflow-hidden rounded-sm ${selected ? 'inset-ring-2 inset-ring-yellow-500' : ''}`,
                disabled && 'cursor-not-allowed',
                className,
                getAccessibleFocusStyles(),
            )}
            onMouseEnter={(e) => {
                if (!disabled) setIsHovering(true);
                onMouseEnter?.(e);
            }}
            onMouseLeave={(e) => {
                setIsHovering(false);
                onMouseLeave?.(e);
            }}
            onClick={(e) => {
                if (!disabled) onSelect?.();
                onClick?.(e);
            }}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled || undefined}
            onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                    e.preventDefault();
                    onSelect?.();
                }
                onKeyDown?.(e);
            }}
            data-testid="image-results-card"
            {...rest}
        >
            {!isLoaded && (
                <Skeleton className="absolute inset-0" animation="shimmer" data-testid="image-results-card-skeleton" />
            )}
            <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                sizes="(max-width: 768px) 50vw, 25vw"
                className="object-cover"
                onLoad={() => setIsLoaded(true)}
                onError={() => setIsLoaded(true)}
            />

            {isHovering && !selected && <HoverOverlay />}
            {selected && <SelectedOverlay />}
        </div>
    );
}
